import { useState, useEffect, useReducer, useContext, useRef } from "react";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_WHATSAPPS") {
    const whatsApps = action.payload;

    return [...whatsApps];
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex] = whatsApp;
      return [...state];
    }

    return [whatsApp, ...state];
  }

  if (action.type === "UPDATE_SESSION") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex].status = whatsApp.status;
      state[whatsAppIndex].updatedAt = whatsApp.updatedAt;
      state[whatsAppIndex].qrcode = whatsApp.qrcode;
      state[whatsAppIndex].retries = whatsApp.retries;
    }

    return [...state];
  }

  if (action.type === "DELETE_WHATSAPPS") {
    const whatsAppId = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsAppId);

    if (whatsAppIndex !== -1) {
      state.splice(whatsAppIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const { user, socket, loading: authLoading } = useContext(AuthContext);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const token = localStorage.getItem("token");
    const hasUser = Boolean(user?.id && user?.companyId);

    if (authLoading) {
      return () => {
        isActive = false;
      };
    }

    if (!hasUser || !token) {
      dispatch({ type: "RESET" });

      if (isMountedRef.current) {
        setLoading(false);
      }

      return () => {
        isActive = false;
      };
    }

    setLoading(true);

    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/?session=0");

        if (!isActive || !isMountedRef.current) return;

        dispatch({
          type: "LOAD_WHATSAPPS",
          payload: Array.isArray(data) ? data : [],
        });
      } catch (_) {
        if (!isActive || !isMountedRef.current) return;

        dispatch({ type: "RESET" });
      } finally {
        if (isActive && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchSession();

    return () => {
      isActive = false;
    };
  }, [authLoading, user]);

  useEffect(() => {
    if (!user?.companyId || !socket?.on || !socket?.off) {
      return undefined;
    }

    const companyId = user.companyId;

    const onCompanyWhatsapp = (data) => {
      if (!isMountedRef.current) return;

      if (data.action === "update") {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
      }
    };

    const onCompanyWhatsappSession = (data) => {
      if (!isMountedRef.current) return;

      if (data.action === "update") {
        dispatch({ type: "UPDATE_SESSION", payload: data.session });
      }
    };

    socket.on(`company-${companyId}-whatsapp`, onCompanyWhatsapp);
    socket.on(`company-${companyId}-whatsappSession`, onCompanyWhatsappSession);

    return () => {
      socket.off(`company-${companyId}-whatsapp`, onCompanyWhatsapp);
      socket.off(`company-${companyId}-whatsappSession`, onCompanyWhatsappSession);
    };
  }, [socket, user?.companyId]);

  return { whatsApps, loading };
};

export default useWhatsApps;
